// API service for fetching location and sensor data

export enum LocationType{
  Sensor,
  Buoy
};

export type ApiLocation = {
  type: LocationType,
  ID: string;
  gCoord: {
    x: number; // latitude
    y: number; // longitude
  };
  siteName: string;
  siteDescription: string;
};

export type ApiResponse = {
  locations?: ApiLocation[];
  rows?: ApiLocation[];
};

function asLocationList(data: unknown): ApiLocation[] {
  if (Array.isArray(data)) return data as ApiLocation[];
  if (data && typeof data === 'object') {
    const record = data as ApiResponse;
    if (Array.isArray(record.locations)) return record.locations;
    if (Array.isArray(record.rows)) return record.rows;
  }
  return [];
}

export type SensorData = {
  timestamp: string;
  value: number;
  sensorID: number;
  locationID: string;
  sensorName: string;
  unit: string;
  // Add other fields as needed
};

export type SensorReadings = {
  [key: string]: {
    readings: SensorData[];          // Full history array for charts
    latestValue: string | null;      // The formatted average with unit (for display)
    rawAverage: number | null;       // The raw average value (without formatting)
    rawLatest: number | null;        // The raw latest value (without formatting)
    unit: string;                    // The unit for this sensor
    latestValidTimestamp?: string | null; // Most recent non-zero timestamp
  };
};

// Add interfaces for the API response structure
interface SensorMetadata {
  sensorID: number;
  sensorName: string;
  sensorUnitAbbreviation: string;
  values: string[];
}

interface ApiPayload {
  sensors?: SensorMetadata[];
  /** Some older API versions wrapped the sensors array under `rows` instead. */
  rows?: SensorMetadata[];
  [key: string]: any;
}

// Dev: /seawall is proxied by Vite → https://seawall.fiu.edu
const SEAWALL_API = import.meta.env.VITE_SEAWALL_API_URL ?? '/seawall';

/**
 * Fetches all available monitoring locations
 */
export async function fetchLocations(): Promise<ApiLocation[]> {
  try {
    const response = await fetch(`${SEAWALL_API}/locations/`);
    if (!response.ok) {
      throw new Error(`Locations API responded with ${response.status}`);
    }
    return asLocationList(await response.json());
  } catch (error) {
    console.error('Error fetching locations:', error);
    throw error;
  }
}

/**
 * Safely converts a value to string, handling undefined/null values
 */
function safeToString(value: any): string {
  if (value === null || value === undefined) {
    return 'N/A';
  }
  try {
    return String(value);
  } catch (e) {
    return 'N/A';
  }
}

/**
 * Fetches sensor data for a specific location
 * @param locationId - The ID of the location to fetch data for
 */
export async function fetchSensorData(locationId: string): Promise<SensorReadings> {
  try {
    debug(`🔍 Fetching data for location ${locationId} from API...`);
    
    // Log the exact URL we're hitting
    const url = `${SEAWALL_API}/frontend/map/${locationId}`;
    debug(`📡 Requesting data from: ${url}`);
    
    const response = await fetch(url);
    
    // Check if the response is OK
    if (!response.ok) {
      console.error(`❌ API responded with status ${response.status}: ${response.statusText}`);
      throw new Error(`API responded with status ${response.status}: ${response.statusText}`);
    }
    
    debug(`✅ API request successful with status ${response.status}`);
    
    // Get response as text first to inspect raw data
    const responseText = await response.text();
    
    // Log the first 500 chars of the response to see what we're getting
    debug(`📄 Raw response text (first 500 chars): ${responseText.substring(0, 500)}...`);
    
    // Try to parse the JSON
    let payload;
    try {
      payload = JSON.parse(responseText) as ApiPayload | SensorMetadata[];
      debug(`✅ Response successfully parsed as JSON`);
    } catch (err) {
      console.error(`❌ Failed to parse response as JSON:`, err);
      return {};
    }
    
    // If payload is missing entirely, return empty result
    if (!payload || typeof payload !== 'object') {
      console.warn(`❌ Invalid data returned from API for location ${locationId}`);
      return {};
    }
    
    // The API has shipped a couple of different envelopes over time:
    //   - current: a bare array of sensors, e.g. [ { sensorID, sensorName, ... }, ... ]
    //   - older:   { sensors: [ ... ] } or { rows: [ ... ] }
    // Normalize to a plain array so the rest of this function doesn't care.
    const sensors: SensorMetadata[] = Array.isArray(payload)
      ? payload
      : (payload as ApiPayload).sensors ?? (payload as ApiPayload).rows ?? [];
    
    debug(`📊 Response structure:`, Array.isArray(payload) ? '(bare array)' : Object.keys(payload));
    
    if (!Array.isArray(sensors)) {
      console.warn(`❌ Could not find a sensors array in the response!`);
      debug(`🔍 Available properties:`, Object.keys(payload));
      return {};
    }
    
    if (sensors.length === 0) {
      console.warn(`❌ 'sensors' array is empty!`);
      return {};
    }
    
    debug(`✅ Found ${sensors.length} sensors in response`);
    
    // Log first sensor structure to see its properties
    if (sensors.length > 0) {
      debug(`📊 First sensor structure:`, sensors[0]);
    }
    
    // Process the sensor data into a more usable format
    const sensorReadings: SensorReadings = {};
    
    // 2. Map each sensor by name to a UI key (case-insensitive)
    const sensorNameMap: {[key: string]: string} = {
      "humidity": "HUMIDITY",
      "temperature": "TEMPERATURE",
      "turbidity": "TURBIDITY",
      "salinity": "SALINITY",
      "total dissolved solids": "TOTAL DISSOLVED SOLIDS",
      "conductivity": "CONDUCTIVITY",
      "ph": "pH",
      "dissolved oxygen": "DISSOLVED OXYGEN"
    };
    
    // Map sensor units
    const sensorUnitMap: {[key: string]: string} = {
      "humidity": "%",
      "temperature": "°C",
      "turbidity": "NTU",
      "salinity": "PSU",
      "total dissolved solids": "ppm",
      "conductivity": "mg/L",
      "ph": "",
      "dissolved oxygen": "US/L"
    };
    
    // Iterate through each sensor in the sensors array
    sensors.forEach((sensor: SensorMetadata) => {
      try {
        if (!sensor || !sensor.sensorName) {
          console.warn("Skipping sensor with no name");
          return; // Skip this sensor
        }
        
        const sensorNameLower = sensor.sensorName.toLowerCase();
        const displayKey = sensorNameMap[sensorNameLower] || sensor.sensorName.toUpperCase();
        
        // Log the mapping for debugging
        debug("Mapping sensorName", sensor.sensorName, "→", displayKey);
        
        // 3. Extract the readings array correctly
        if (!Array.isArray(sensor.values) || sensor.values.length === 0) {
          debug(`No readings found for sensor ${displayKey}`);
          return; // Skip this sensor
        }
        
        // Check if values exists and is an array
        if (!Array.isArray(sensor.values)) {
          console.warn(`❌ No 'values' array found for sensor ${displayKey}!`);
          debug(`🔍 Sensor structure:`, sensor);
          return; // Skip this sensor
        }
        
        // Log raw values before any processing
        debug(`📊 Raw values for ${displayKey}:`, sensor.values);
        
        // STEP A: Map - Extract the 'value' property from each reading object and parse it
        const parsedValues = sensor.values.map((reading: any, index: number) => {
          // Log the raw reading object to see its structure
          debug(`🔍 Raw reading object [${index}]:`, reading);
          
          // Check if reading is an object with a value property
          if (!reading || typeof reading !== 'object') {
            console.warn(`❌ Reading at index ${index} is not an object:`, reading);
            return NaN;
          }
          
          // Extract the value string from the reading object
          const raw = reading.value;
          debug(`🔢 reading.value is:`, raw);
          
          if (typeof raw !== 'string') {
            console.warn(`❌ Value at index ${index} is not a string:`, raw);
            return NaN;
          }
          
          // Trim any whitespace and parse
          const trimmed = raw.trim();
          const num = parseFloat(trimmed);
          debug(`🔢 Parsed number [${index}]: ${num}`);
          return num;
        });
        
                // STEP B: Filter - Remove NaN and sentinel values (-1023), but keep zeros
        const validReadings = parsedValues.filter((value: number) => {
          if (value === 0) {
            debug(`✅ Keeping zero value`);
            return true;
          } else if (isNaN(value)) {
            debug(`❌ Filtering out NaN value`);
            return false;
          } else if (value === -1023) {
            debug(`❌ Filtering out sentinel value (-1023)`);
            return false;
          }
          return true;
        });
        
        // Log valid readings for debugging
        debug(`✅ Valid readings for ${displayKey} → ${validReadings.length}: [${validReadings.join(', ')}]`);

        if (validReadings.length === 0) {
          debug(`❌ No valid readings for sensor ${displayKey} after filtering`);
          return; // Skip this sensor
        }
        
        // STEP C: Reduce - Compute metrics
        
        // Get latest value (first in the array is most recent)
        const latest = validReadings[0];
        const formattedLatest = latest.toFixed(1);
        
        // Calculate average (this is what we'll display)
        const avg = validReadings.length 
          ? validReadings.reduce((sum, x) => sum + x, 0) / validReadings.length 
          : null;
        
        // Format the display value - use average, fallback to latest if needed
        const displayValue = avg !== null 
          ? avg.toFixed(1) 
          : formattedLatest;
        
        // Log both values
        debug(`⭐ Latest ${displayKey} reading: ${formattedLatest}`);
        debug(`⭐ Display value for ${displayKey} (average): ${displayValue}`);
        debug(`⭐ Readings count: ${validReadings.length}`);
        
        // Get the proper unit for this sensor
        const unit = sensor.sensorUnitAbbreviation || sensorUnitMap[sensorNameLower] || '';
        debug(`📏 Unit for ${displayKey}: "${unit}"`);
        
        // Create sensor readings array using real timestamps from the reading objects
        const readingsArray = validReadings.map((value: number, index: number) => {
          // Get the original reading object to extract its timestamp
          const originalReading = sensor.values[index] as any;
          const timestamp = originalReading && originalReading.dateTime ? 
                           originalReading.dateTime : 
                           (new Date(Date.now() - index * 3600000)).toISOString(); // Fallback to mock timestamp
          
          return {
            timestamp,
            value: value, // Already a valid number
            sensorID: sensor.sensorID || 0,
            locationID: locationId,
            sensorName: sensor.sensorName,
            unit: unit
          };
        });
        
        // Find the most recent non-zero, non-sentinel reading for timestamps
        const latestValidReading = (sensor.values as any[])
          .filter(r => r && typeof r === 'object' && r.value && 
                 Number(r.value) !== 0 && Number(r.value) !== -1023)
          .sort((a: any, b: any) => {
            // Sort by dateTime, most recent first
            if (!a.dateTime || !b.dateTime) return 0;
            return new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime();
          })[0];
        
        // Get the timestamp from the latest valid reading if available
        const latestValidTimestamp = latestValidReading && latestValidReading.dateTime
          ? new Date(latestValidReading.dateTime).toLocaleString()
          : null;
        
        if (latestValidTimestamp) {
          debug(`⏰ Latest valid timestamp for ${displayKey}: ${latestValidTimestamp}`);
        }
        
        // Format final display value with unit
        // const finalValue = validReadings.length > 0 ? `${displayValue}${unit}` : null;
        const finalValue = validReadings.length > 0 ? `${displayValue}` : null;
        
        const currentSensorProcessedData = {
          readings: readingsArray,         // Full history array
          latestValue: finalValue,         // Formatted average with unit
          rawAverage: avg,                 // Raw average value
          rawLatest: latest,               // Raw latest value
          unit,                            // Unit for this sensor
          latestValidTimestamp             // Most recent non-zero timestamp
        };

        debug(`🔒 Pre-assignment for [${displayKey}]:`, JSON.stringify(currentSensorProcessedData));
        sensorReadings[displayKey] = currentSensorProcessedData;
        
        // Log the final output for this sensor
        debug(`✅ ${displayKey} final value in sensorReadings: ${sensorReadings[displayKey]?.latestValue}`);
        debug(`✅ ${displayKey} readings in sensorReadings: ${sensorReadings[displayKey]?.readings?.length} values`);
      } catch (error) {
        console.error(`Error processing sensor ${sensor?.sensorName}:`, error);
      }
    });
    
    // Final dashboard summary - one clean state object
    debug("Dashboard params (full object before return):", JSON.stringify(sensorReadings, null, 2));
    
    // Log which sensors were found vs missing
    const foundSensorTypes = Object.keys(sensorReadings);
    const missingSensorTypes = Object.keys(sensorNameMap)
      .filter(key => !foundSensorTypes.includes(sensorNameMap[key]));
    
    debug(`Found ${foundSensorTypes.length} sensors, missing ${missingSensorTypes.length}`);
    if (missingSensorTypes.length > 0) {
      debug("Missing sensor types:", missingSensorTypes.map(t => sensorNameMap[t]));
    }
    
    return sensorReadings;
  } catch (error) {
    console.error(`Error fetching sensor data for location ${locationId}:`, error);
    // Return empty object instead of throwing, so the UI can handle it gracefully
    debug(`❌ Returning empty object due to error in fetchSensorData for location ${locationId}`);
    return {};
  }
}

/**
 * Formats timestamp for display
 */
export function formatTimestamp(timestamp: string): string {
  try {
    const date = new Date(timestamp);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }
    
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  } catch (e) {
    console.warn('Error formatting timestamp:', e);
    return timestamp || 'Unknown';
  }
}

/**
 * Prepare chart data from sensor readings
 */
export function prepareChartData(readings: SensorData[]): Array<{ time: string; value: number }> {
  try {
    if (!Array.isArray(readings) || readings.length === 0) {
      return [];
    }
    
    return readings
      .filter(reading => reading && reading.timestamp && reading.value !== undefined)
      .map(reading => ({
        // Readings can span months or even years apart, so the axis label
        // needs the date (including year) as well as the time — time alone
        // made distant readings indistinguishable on the chart, and some
        // sites have readings from different years that would otherwise
        // look identical without a year.
        time: new Date(reading.timestamp).toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          year: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        }),
        value: Number(reading.value)
      }))
      .reverse(); // Reverse to get chronological order
  } catch (e) {
    console.error('Error preparing chart data:', e);
    return [];
  }
} 